import React from "react";
import { Button } from "@/components/agent/Button";
import MonacoEditor from "@/components/agent/MonacoEditor";
import Checkbox from "@/components/atomic/Checkbox";
import { Input } from "@/components/atomic/Input";
import Label from "@/components/atomic/Label";
import SelectBox from "@/components/atomic/SelectBox";
import TitleBar from "@/components/ui/TitleBar";

interface BasePromptEditFormProps {
  title: string;
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  instruction: string;
  setInstruction: (value: string) => void;
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  selectedWorkspaceId: string;
  setSelectedWorkspaceId: (value: string) => void;
  selectedUserScope: string;
  setSelectedUserScope: (value: string) => void;
  workspaceOptions: Array<{ value: string; label: string; }>;
  userScopeOptions: Array<{ value: string; label: string; }>;
  isSaving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  namePlaceholder?: string;
  additionalFields?: React.ReactNode;
}

export const BasePromptEditForm: React.FC<BasePromptEditFormProps> = ({
  title,
  name,
  setName,
  description,
  setDescription,
  instruction,
  setInstruction,
  enabled,
  setEnabled,
  selectedWorkspaceId,
  setSelectedWorkspaceId,
  selectedUserScope,
  setSelectedUserScope,
  workspaceOptions,
  userScopeOptions,
  isSaving,
  onSubmit,
  onCancel,
  namePlaceholder = "Enter name",
  additionalFields,
}) => {
  return (
    <div className="">
      <TitleBar title={title} />

      <div className="h-[calc(100vh-100px)] p-8 flex flex-col gap-5 w-full max-w-[1320px]">
        {/* Name and Description - Side by Side */}
        <div className="flex gap-4">
          <div className="space-y-2 w-[360px]">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder={namePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2 flex-1">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        {/* Scope - Two SelectBoxes Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-scope">Scope (Workspace)</Label>
            <SelectBox
              currentValue={selectedWorkspaceId}
              options={workspaceOptions}
              onValueChange={(value) => {
                console.log("Workspace changed to:", value);
                setSelectedWorkspaceId(value);
              }}
              placeholder="Select workspace"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="user-scope">Scope (User)</Label>
            <SelectBox
              currentValue={selectedUserScope}
              options={userScopeOptions}
              onValueChange={(value) => {
                console.log("User scope changed to:", value);
                setSelectedUserScope(value);
              }}
              placeholder="Select user scope"
            />
          </div>
        </div>

        {/* Instruction */}
        <div className="space-y-2 flex flex-col flex-1">
          <Label>Instruction</Label>
          <MonacoEditor
            value={instruction}
            onChange={(value) => setInstruction(value || "")}
          />
        </div>

        {/* Additional Fields Slot */}
        {additionalFields}

        {/* Active */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="enabled"
            checked={enabled}
            onCheckedChange={(checked) => setEnabled(checked as boolean)}
          />
          <Label htmlFor="enabled" className="cursor-pointer">Active</Label>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!name || !description || !instruction || isSaving}
            className="bg-[#6888E1] hover:bg-[#6888E1]/90 text-white"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};
